/**
 * Mega-menu panel controller.
 *
 * Server-side, mega-menu blocks render as <div class="mega-menu-panel" data-mega-menu-handle="...">
 * children of <header-menu>, alongside the menu list. They are positioned absolutely and hidden
 * by default. This script:
 *
 *   1. Finds every panel and its matching top-level <li data-link-handle="...">.
 *   2. Sets aria-haspopup / aria-controls on the link so a11y trees see the relationship.
 *   3. Toggles .is-active on the matching panel when the link is hovered, focused, or the panel
 *      itself is hovered. Nothing else: no DOM moves, no element creation, no offsetHeight
 *      measurement. Surviving a Shopify morph means only the .is-active class is at risk, and
 *      that only matters during an active hover.
 *
 * Re-runs automatically on shopify:section:load, shopify:block:select, shopify:block:deselect,
 * and whenever the <header-menu> subtree mutates (covers HMR-style replacements).
 */

const CLOSE_DELAY = 200;

const normalize = (v) => (v || '').trim().toLowerCase();

class MegaMenuController {
  constructor(headerMenu) {
    this.headerMenu = headerMenu;
    this.activePanel = null;
    this.closeTimer = null;
    this.boundHandlers = new WeakMap();

    this.wire();

    this.observer = new MutationObserver(() => this.wire());
    this.observer.observe(headerMenu, { childList: true, subtree: true });

    document.addEventListener('keydown', this.handleKeydown);
    document.addEventListener('click', this.handleDocumentClick);
  }

  /**
   * Find all panels, pair each with its top-level <li>, and attach listeners idempotently.
   */
  wire = () => {
    const panels = this.headerMenu.querySelectorAll('.mega-menu-panel[data-mega-menu-handle]');

    panels.forEach((panel) => {
      const handle = normalize(panel.dataset.megaMenuHandle);
      if (!handle) return;

      const li = this.headerMenu.querySelector(`[data-link-handle="${CSS.escape(handle)}"]`);
      if (!li) return;

      const link = li.querySelector('[ref="menuitem"]');
      if (link) {
        link.setAttribute('aria-haspopup', 'true');
        if (!link.hasAttribute('aria-expanded')) {
          link.setAttribute('aria-expanded', 'false');
        }
        if (!panel.id) {
          panel.id = `mega-menu-panel-${handle}`;
        }
        link.setAttribute('aria-controls', panel.id);
      }

      this.attach(li, panel, 'li-enter', 'pointerenter', () => this.open(panel));
      this.attach(li, panel, 'li-leave', 'pointerleave', () => this.scheduleClose());
      this.attach(li, panel, 'li-focusin', 'focusin', () => this.open(panel));
      this.attach(li, panel, 'li-focusout', 'focusout', (e) => {
        if (!li.contains(e.relatedTarget) && !panel.contains(e.relatedTarget)) {
          this.scheduleClose();
        }
      });

      this.attach(panel, panel, 'panel-enter', 'pointerenter', () => this.cancelClose());
      this.attach(panel, panel, 'panel-leave', 'pointerleave', () => this.scheduleClose());
    });
  };

  /**
   * Idempotent listener attach: keyed by (element, panel, eventName) so re-running wire()
   * after a morph doesn't pile up duplicate listeners.
   */
  attach(element, panel, key, eventName, handler) {
    let map = this.boundHandlers.get(element);
    if (!map) {
      map = new Map();
      this.boundHandlers.set(element, map);
    }
    const compositeKey = `${key}:${panel.id || ''}`;
    if (map.has(compositeKey)) return;
    map.set(compositeKey, handler);
    element.addEventListener(eventName, handler);
  }

  open(panel) {
    this.cancelClose();
    this.positionAnchoredPanel(panel);
    const previous = this.activePanel;
    if (previous && previous !== panel) {
      this.crossfade(previous, panel);
    } else {
      this.setPanelState(panel, true);
    }
    this.activePanel = panel;
  }

  /**
   * For panels in "anchor to trigger" mode, set --mega-menu-anchor-left so the
   * panel's left edge sits under the trigger link. Measured against the panel's
   * offsetParent so it works regardless of where the absolute container lives.
   */
  positionAnchoredPanel(panel) {
    if (!panel.classList.contains('mega-menu--anchor-trigger')) return;
    const handle = normalize(panel.dataset.megaMenuHandle);
    const li = this.headerMenu.querySelector(`[data-link-handle="${CSS.escape(handle)}"]`);
    if (!li) return;
    const offsetParent = panel.offsetParent || document.documentElement;
    const parentRect = offsetParent.getBoundingClientRect();
    const liRect = li.getBoundingClientRect();
    const left = Math.max(0, liRect.left - parentRect.left);
    panel.style.setProperty('--mega-menu-anchor-left', `${left}px`);
  }

  /**
   * Hand off directly between two panels by pinning the outgoing at its
   * current height and tweening the incoming from that height to its own
   * natural height. Left/width are NOT animated — the incoming snaps to its
   * natural position so the right edge doesn't visibly slide in when going
   * between panels of different widths (e.g. full-width → anchored compact).
   * Visibility: hidden preserves layout, so the incoming's natural offsetHeight
   * is computable even while it's inactive.
   */
  crossfade(previous, next) {
    if (this.cleanupTimer) {
      clearTimeout(this.cleanupTimer);
      this.cleanupTimer = null;
      // If a prior crossfade is still in flight, clear any inline heights it
      // left behind so we measure fresh natural sizes below.
      previous.style.height = '';
      next.style.height = '';
      previous.classList.remove('is-switching', 'is-leaving');
      next.classList.remove('is-switching', 'is-leaving');
    }

    const fromHeight = previous.offsetHeight;
    const toHeight = next.offsetHeight;

    previous.style.height = `${fromHeight}px`;
    previous.classList.add('is-leaving');

    next.style.height = `${fromHeight}px`;
    next.classList.add('is-switching');

    this.setPanelState(next, true);
    this.setPanelState(previous, false);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        next.style.height = `${toHeight}px`;
      });
    });

    this.cleanupTimer = setTimeout(() => {
      next.style.height = '';
      next.classList.remove('is-switching');
      previous.style.height = '';
      previous.classList.remove('is-leaving');
      this.cleanupTimer = null;
    }, 360);
  }

  close() {
    if (!this.activePanel) return;
    this.setPanelState(this.activePanel, false);
    this.activePanel = null;
  }

  setPanelState(panel, active) {
    panel.classList.toggle('is-active', active);
    panel.setAttribute('aria-hidden', active ? 'false' : 'true');

    const handle = normalize(panel.dataset.megaMenuHandle);
    const li = this.headerMenu.querySelector(`[data-link-handle="${CSS.escape(handle)}"]`);
    const link = li?.querySelector('[ref="menuitem"]');
    if (link) {
      link.setAttribute('aria-expanded', active ? 'true' : 'false');
    }
  }

  scheduleClose() {
    this.cancelClose();
    this.closeTimer = setTimeout(() => this.close(), CLOSE_DELAY);
  }

  cancelClose() {
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  }

  handleKeydown = (event) => {
    if (event.key === 'Escape' && this.activePanel) {
      this.close();
    }
  };

  handleDocumentClick = (event) => {
    if (!this.activePanel) return;
    const target = event.target;
    if (this.activePanel.contains(target)) return;
    if (this.headerMenu.contains(target) && target.closest('[data-link-handle]')) return;
    this.close();
  };
}

const controllers = new WeakMap();

function init(root = document) {
  const scope = root.nodeType === 1 || root.nodeType === 9 || root.nodeType === 11 ? root : document;
  scope.querySelectorAll?.('header-menu').forEach((el) => {
    if (!controllers.has(el)) {
      controllers.set(el, new MegaMenuController(el));
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => init());
} else {
  init();
}

document.addEventListener('shopify:section:load', (event) => {
  if (event.target) init(event.target);
});
document.addEventListener('shopify:block:select', () => init());
document.addEventListener('shopify:block:deselect', () => init());
